import React, { useEffect, useState } from "react";
import { History } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { techStacksList } from "../../utils/common";
import DataTable from "../Common/DataTable";
import {
  PageHeader,
  FilterButton, ActiveFilterChips, FilterPanel, FilterField, Input, FilterRow, FilterSelectField,
  useListFilters,
} from "../ui";
import "./AI.scss";

const techStacks = techStacksList;
const difficulties = ["", "easy", "medium", "hard"];
const DEFAULT_PAGE_SIZE = 10;

const StudentAttemptHistory = () => {
  const student = getCurrentUser();
  const [attempts, setAttempts] = useState([]);
  // Declared above useListFilters, whose onChange closes over setPage. Safe either
  // way (a callback body, not a deps array) but it keeps no-use-before-define quiet.
  const [page, setPage] = useState(1);
  /* The FilterPanel pattern (D3). Same shape as AdminAIAttemptHistory, including
     the same 🔴 defect: minScore/maxScore had no desktop control — they lived
     only in the `d-md-none` modal, so a student on a laptop could not reach
     them while the badge still counted them. */
  const listFilters = useListFilters(
    {
      techStack: {
        label: "Tech Stack", empty: "",
        options: techStacks.map((t) => ({ value: t, label: t || "All Tech Stacks" })),
      },
      difficulty: {
        label: "Difficulty", empty: "",
        options: difficulties.map((d) => ({ value: d, label: d || "All Difficulties" })),
      },
      minScore: { label: "Min Score", empty: "" },
      maxScore: { label: "Max Score", empty: "" },
    },
    () => setPage(1),
  );
  const filters = listFilters.values;
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchAttempts = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/ai/ai-attempts/history", {
        params: {
          studentId: student?.studentId,
          page,
          limit: pageSize,
          ...filters
        }
      });
      setAttempts(data.attempts || []);
      setTotalRecords(data.total || 0);
    } catch {
      setAttempts([]);
      setTotalRecords(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (student?.studentId) fetchAttempts();
  }, [filters, page, pageSize]);

  const getScoreClass = (score) => {
    if (score >= 70) return "ai-score-badge--high";
    if (score >= 40) return "ai-score-badge--medium";
    return "ai-score-badge--low";
  };

  const getDifficultyClass = (difficulty) => {
    const d = difficulty?.toLowerCase();
    if (d === "easy") return "ai-difficulty-badge--easy";
    if (d === "medium") return "ai-difficulty-badge--medium";
    if (d === "hard") return "ai-difficulty-badge--hard";
    return "";
  };

  // Define table columns
  const columns = [
    {
      key: "index",
      label: "#",
      width: "50px",
      render: (_, index) => (page - 1) * pageSize + index + 1
    },
    {
      key: "question",
      label: "Question",
      width: "25%",
      render: (a) => (
        <div className="fw-medium" title={a.question?.question}>
          {a.question?.question?.length > 80 
            ? a.question.question.substring(0, 80) + "..." 
            : (a.question?.question || "-")}
        </div>
      )
    },
    {
      key: "answer",
      label: "Your Answer",
      width: "20%",
      render: (a) => (
        <div className="text-sm" title={a.answer}>
          {a.answer?.length > 80 ? a.answer.substring(0, 80) + "..." : (a.answer || "-")}
        </div>
      )
    },
    {
      key: "techStack",
      label: "Tech Stack",
      width: "10%",
      render: (a) => a.question?.techStack || "-"
    },
    {
      key: "difficulty",
      label: "Difficulty",
      width: "10%",
      render: (a) => (
        <span className={`ai-difficulty-badge ${getDifficultyClass(a.question?.difficulty)}`}>
          {a.question?.difficulty || "-"}
        </span>
      )
    },
    {
      key: "score",
      label: "Score",
      width: "8%",
      render: (a) => (
        <span className={`ai-score-badge ${getScoreClass(a.score)}`}>
          {a.score ?? "-"}
        </span>
      )
    },
    {
      key: "feedback",
      label: "Feedback",
      width: "20%",
      render: (a) => (
        <div className="text-sm" title={a.feedback}>
          {a.feedback?.length > 80 ? a.feedback.substring(0, 80) + "..." : (a.feedback || "-")}
        </div>
      )
    },
    {
      key: "submittedOn",
      label: "Submitted On",
      width: "12%",
      render: (a) => a.createdAt 
        ? new Date(a.createdAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) 
        : "-"
    }
  ];

  return (
    <div className="ai-page">
      <PageHeader
        icon={History}
        title="My AI Attempts"
        description="View your AI interview practice history and scores"
      />

      {/* Compact Toolbar */}
      <div className="ai-toolbar">
        {/* One filter UI on both viewports — this was an inline strip carrying two
            of the four filters beside a mobile-only modal carrying all four. */}
        <div className="ai-toolbar-actions" style={{ marginLeft: "auto" }}>
          <FilterButton count={listFilters.count} onClick={listFilters.open} />
        </div>
      </div>

      <ActiveFilterChips items={listFilters.activeFilters} onClearAll={listFilters.reset} />

      {/* Data Table */}
      <DataTable
        data={attempts}
        columns={columns}
        loading={loading}
        hoverable={true}
        pagination={{
          page,
          pageSize,
          total: totalRecords,
          onPageChange: setPage
        }}
        emptyMessage="No AI attempts found"
      />

      <FilterPanel {...listFilters.panelProps} title="Filter Attempts">
        <FilterSelectField {...listFilters.selectProps("techStack")} />
        <FilterSelectField {...listFilters.selectProps("difficulty")} />
        <FilterRow>
          <FilterField label="Min Score">
            <Input size="lg"
              type="number"
              value={listFilters.values.minScore}
              onChange={(e) => listFilters.set("minScore", e.target.value)}
              placeholder="Min Score"
            />
          </FilterField>
          <FilterField label="Max Score">
            <Input size="lg"
              type="number"
              value={listFilters.values.maxScore}
              onChange={(e) => listFilters.set("maxScore", e.target.value)}
              placeholder="Max Score"
            />
          </FilterField>
        </FilterRow>
      </FilterPanel>
    </div>
  );
};

export default StudentAttemptHistory;
